import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Branches.css';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    google_maps_url: '',
    description: '',
    city: '',
    country_code: ''
  });
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/branches');
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate URL
    if (!formData.google_maps_url.includes('maps.app.goo.gl')) {
      alert('Please enter a valid Google Maps share link (maps.app.goo.gl)');
      return;
    }

    try {
      if (editingBranch) {
        // Update existing branch
        await api.put(`/api/branches/${editingBranch.id}`, formData);
      } else {
        // Create new branch
        await api.post('/api/branches', formData);
      }
      
      fetchBranches();
      closeModal();
    } catch (error) {
      console.error('Error saving branch:', error);
      alert('Error saving branch. Please try again.');
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      google_maps_url: branch.google_maps_url,
      description: branch.description || '',
      city: branch.city || '',
      country_code: branch.country_code || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (branchId) => {
    const branchName = showDeleteConfirm?.name || 'Branch';
    
    console.log('🗑️ Starting deletion for branch:', branchId, branchName);
    
    try {
      console.log('🗑️ Sending delete request...');
      const response = await api.delete(`/api/branches/${branchId}`);
      console.log('🗑️ Delete response:', response);
      
      if (response.data.success) {
        console.log('✅ Delete successful, showing success toast');
        showSuccess(`Branch Deleted Successfully: ${branchName} and all its reviews have been permanently deleted.`, 5000);
      } else {
        console.log('❌ Delete failed with response:', response.data);
        throw new Error(response.data.error || 'Failed to delete branch');
      }
      
      console.log('🔄 Refreshing branches and closing modal...');
      fetchBranches();
      setShowDeleteConfirm(null);
      setDeleteConfirmName('');
    } catch (error) {
      console.error('❌ Error deleting branch:', error);
      showError(`Failed to Delete Branch: Could not delete ${branchName}. ${error.response?.data?.error || error.message}`, 6000);
    }
  };

  const openModal = () => {
    setEditingBranch(null);
    setFormData({
      name: '',
      google_maps_url: '',
      description: '',
      city: '',
      country_code: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBranch(null);
  };


  if (loading) {
    return (
      <div className="branches-loading">
        <div className="loading-spinner"></div>
        <p>Loading branches...</p>
      </div>
    );
  }

  return (
    <div className="branches">
      <div className="branches-header">
        <div className="header-content">
          <h1 className="branches-title">Branch Management</h1>
          <p className="branches-subtitle">
            Manage your business locations and their Google Maps links
          </p>
        </div>
        <button 
          className="add-branch-btn glass-effect hover-glow"
          onClick={openModal}
        >
          <Plus size={16} />
          Add Branch
        </button>
      </div>

      {/* Branches Grid */}
      <div className="branches-grid">
        {branches.map((branch, index) => (
          <motion.div
            key={branch.id}
            className="branch-card glass-effect"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <div className="branch-header">
              <h3 className="branch-name">{branch.name}</h3>
              <div className="branch-actions">
                <button 
                  className="branch-action-btn edit-btn"
                  onClick={() => handleEdit(branch)}
                  title="Edit branch"
                >
                  <Edit size={16} />
                </button>
                <button 
                  className="branch-action-btn delete-btn"
                  onClick={() => setShowDeleteConfirm(branch)}
                  title="Delete branch"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="branch-details">
              {branch.description && (
                <p className="branch-description">{branch.description}</p>
              )}
              
              {(branch.city || branch.country_code) && (
                <div className="branch-location">
                  <MapPin size={14} />
                  <span>{branch.city}{branch.city && branch.country_code && ', '}{branch.country_code}</span>
                </div>
              )}

              <div className="branch-stats">
                <div className="stat">
                  <span className="stat-label">Reviews:</span>
                  <span className="stat-value">{branch.total_reviews || 0}</span>
                </div>
                {branch.average_rating && (
                  <div className="stat">
                    <span className="stat-label">Rating:</span>
                    <span className="stat-value">{branch.average_rating.toFixed(1)}★</span>
                  </div>
                )}
              </div>

              <a 
                href={branch.google_maps_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="maps-link"
              >
                <ExternalLink size={14} />
                View on Google Maps
              </a>
            </div>
          </motion.div>
        ))}

        {branches.length === 0 && (
          <div className="no-branches">
            <MapPin size={48} />
            <h3>No branches yet</h3>
            <p>Add your first branch to start managing reviews</p>
            <button className="add-first-branch-btn" onClick={openModal}>
              <Plus size={16} />
              Add Your First Branch
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <motion.div 
            className="branch-modal glass-effect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{editingBranch ? 'Edit Branch' : 'Add New Branch'}</h2>
              <button className="close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="branch-form">
              <div className="form-group">
                <label>Branch Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Downtown Branch, Main Location"
                  required
                />
              </div>

              <div className="form-group">
                <label>Google Maps URL *</label>
                <input
                  type="url"
                  value={formData.google_maps_url}
                  onChange={(e) => setFormData({...formData, google_maps_url: e.target.value})}
                  placeholder="https://maps.app.goo.gl/..."
                  required
                />
                <small>Share the location from Google Maps and paste the link here</small>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of this branch..."
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="e.g. Riyadh"
                  />
                </div>

                <div className="form-group">
                  <label>Country Code</label>
                  <input
                    type="text"
                    value={formData.country_code}
                    onChange={(e) => setFormData({...formData, country_code: e.target.value.toUpperCase()})}
                    placeholder="e.g. SA"
                    maxLength="2"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  <Save size={16} />
                  {editingBranch ? 'Update Branch' : 'Add Branch'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <motion.div 
            className="confirm-modal glass-effect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirm-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>Delete Branch</h3>
            </div>
            <p>
              Are you sure you want to delete <strong>{showDeleteConfirm.name}</strong>?
            </p>
            <p className="warning-text">
              ⚠️ This will also delete ALL reviews associated with this branch. This action cannot be undone.
            </p>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Type the branch name to confirm</label>
              <input
                type="text"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={showDeleteConfirm.name.trim()}
                autoFocus
              />
              <small>Enter <strong>{showDeleteConfirm.name.trim()}</strong> exactly to enable deletion.</small>
            </div>
            <div className="confirm-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                disabled={deleteConfirmName.trim() !== showDeleteConfirm.name.trim()}
                onClick={() => handleDelete(showDeleteConfirm.id)}
              >
                <Trash2 size={16} />
                Delete Branch
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Branches;
